import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import debounce from 'lodash/debounce';
// components
import Modal from 'shared/modals/Modal';
import Table from 'shared/tables/Table';
import LoadingModal from 'shared/loading/LoadingModal';
import { Alert } from 'styled-alert-component';
import { FormikInput } from 'shared/forms';
// helpers
import { timeFormatter } from 'shared/../constants/Formatting';
// fetch
import { updateWaitingTime, getWaitingTime } from 'src/fetch/orders';
import { useMediaQuery } from '../../../../shared/hooks';

const WaitingTimeModal = ({ isShowing, onClose, data, type }) => {
  const [stopData, setStopData] = useState([]);
  const [startTime, setStartTime] = useState(data[type].waitArrive && data[type].waitArrive.substring(11, 16));
  const [endTime, setEndTime] = useState(data[type].waitDepart && data[type].waitDepart.substring(11, 16));

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  const isSmall = useMediaQuery('(max-width: 767px)');

  useEffect(() => {
    const rows = [
      {
        label: 'Appointment',
        arrivalTime: data[type].appointmentStartTime,
        departureTime: data[type].appointmentEndTime,
        total: ''
      },
      {
        label: 'Driver',
        arrivalTime: data[type].arrive,
        departureTime: data[type].depart,
        total: data[type].actualTime ? data[type].actualTime : ''
      },
      {
        label: 'Wait Time',
        arrivalTime: data[type].waitArrive ? data[type].waitArrive.substring(11, 16) : '',
        departureTime: data[type].waitDepart ? data[type].waitDepart.substring(11, 16) : '',
        total: data[type].waitTime
      }
    ];
    setStopData(rows);
    setIsLoaded(true);
  }, []);

  const columns = [
    {
      Header: '',
      accessor: 'label',
      width: 150,
      id: 'label',
      Cell: ({ original }) => {
        return original.label === 'Wait Time' ? <div style={{ marginTop: '.4rem' }}>Wait Time</div> : original.label;
      }
    },
    {
      Header: 'Arrive',
      accessor: 'arrivalTime',
      id: 'arrivalTime',
      width: isSmall ? 150 : 230,
      Cell: ({ original }) => {
        return original.label === 'Wait Time' ? (
          <FormikInput
            wrapperClass='wrapper-fill-column'
            name='startTime'
            value={startTime}
            onChange={e => setStartTime(e.target.value)}
            onBlur={e => setStartTime(timeFormatter(e.target.value))}
            disabled={isSubmitting}
          />
        ) : (
          original.arrivalTime
        );
      }
    },
    {
      Header: 'Depart',
      accessor: 'departureTime',
      id: 'departureTime',
      width: isSmall ? 150 : 230,
      Cell: ({ original }) => {
        return original.label === 'Wait Time' ? (
          <FormikInput
            wrapperClass='wrapper-fill-column'
            name='endTime'
            value={endTime}
            onChange={e => setEndTime(e.target.value)}
            onBlur={e => setEndTime(timeFormatter(e.target.value))}
            disabled={isSubmitting}
          />
        ) : (
          original.departureTime
        );
      }
    },
    {
      Header: 'Total',
      accessor: 'total',
      isCentered: true,
      id: 'total',
      Cell: ({ original }) => {
        return original.label === 'Wait Time' ? <div style={{ marginTop: '.4rem' }}>{original.total}</div> : original.total;
      }
    }
  ];

  const updateTableData = async () => {
    const today = new Date().toISOString().substring(0, 10);

    try {
      const response = await getWaitingTime({
        arrive: format(new Date(`${today} ${startTime}`), 'MM/DD/YYYY HH:mm'),
        depart: format(new Date(`${today} ${endTime}`), 'MM/DD/YYYY HH:mm'),
      });

      const rows = [
        {
          label: 'Appointment',
          arrivalTime: data[type].appointmentStartTime,
          departureTime: data[type].appointmentEndTime,
          total: ''
        },
        {
          label: 'Driver',
          arrivalTime: data[type].arrive,
          departureTime: data[type].depart,
          total: data[type].actualTime ? data[type].actualTime : ''
        },
        {
          label: 'Wait Time',
          arrivalTime: data[type].waitArrive ? data[type].waitArrive.substring(11, 16) : '',
          departureTime: data[type].waitDepart ? data[type].waitDepart.substring(11, 16) : '',
          total: response
        }
      ];
      setStopData(rows);
    } catch (error) {
      console.log('error', error);
    }
  };

  const debouncedUpdate = debounce(() => updateTableData(), 500);

  useEffect(() => {
    if (startTime && endTime && isLoaded) {
      debouncedUpdate();
    }
  }, [startTime, endTime]);

  const saveChanges = async (clearWaitTime = false) => {
    setIsSubmitting(true);
    setApiError(null);

    let leg;
    const today = new Date().toISOString().substring(0, 10);

    if (type === 'origin') {
      leg = {
        ...data,
        origin: {
          ...data.origin,
          waitArrive: !clearWaitTime && startTime ? format(new Date(`${today} ${startTime}`), 'MM/DD/YYYY HH:mm') : null,
          waitDepart: !clearWaitTime && endTime ? format(new Date(`${today} ${endTime}`), 'MM/DD/YYYY HH:mm') : null
        }
      };
    } else {
      leg = {
        ...data,
        destination: {
          ...data.destination,
          waitArrive: !clearWaitTime && startTime ? format(new Date(`${today} ${startTime}`), 'MM/DD/YYYY HH:mm') : null,
          waitDepart: !clearWaitTime && endTime ? format(new Date(`${today} ${endTime}`), 'MM/DD/YYYY HH:mm') : null
        }
      };
    }

    try {
      await updateWaitingTime(leg);
      setIsSubmitting(false);
      onClose(true);
    } catch (error) {
      console.log('error', error);
      setIsSubmitting(false);
      setApiError('Something went wrong! Please try again.');
    }
  };

  return (
    <Modal isOpen={isShowing} handleClose={() => onClose()} containerStyle='large'>
      <div className='modal-header'>
        <h5 className='modal-title' id='waitingTimeModal'>
          {`Waiting Time for PRO ${data.proNumber} - Leg ${data.legNumber} - ${type === 'destination' ? 'Destination' : 'Origin'} Appointment`}
        </h5>
        <button className='button large close' onClick={() => onClose()} />
      </div>
      <form>
        <h5 className='text-align--center'>{data[type === 'destination' ? 'destination' : 'origin'].locationName}</h5>
        <section className='modal-body formik-form'>
          <LoadingModal isOpen={isSubmitting} isNested={true} />
          <div className='formik-row' style={{ display: 'flex', justifyContent: 'center' }}>
            <Table
              className='table--primary-color'
              data={stopData}
              defaultPageSize={3}
              isLoading={isSubmitting}
              columns={columns}
              {...(isSmall ? {} : { style: { overflow: 'hidden' } })}
            />
          </div>
        </section>
        <section className='modal-footer toolbar toolbar--xxs'>
          <div className='toolbar--column toolbar--right'>
            <button
              type='button'
              className='button large secondary text-align--center toolbar--button--margin'
              disabled={isSubmitting}
              onClick={() => saveChanges()}
            >
              Confirm
            </button>
            <button
              type='reset'
              id='driver-truck-assignment-cancel'
              className='button large white text-align--center toolbar--button--margin'
              onClick={() => {
                saveChanges(true);
              }}
            >
              Clear Wait Time
            </button>
            <button
              type='reset'
              id='driver-truck-assignment-cancel'
              className='button large white text-align--center margin--none--xxs'
              onClick={() => onClose()}
            >
              Cancel
            </button>
          </div>
        </section>
      </form>
      {!isSubmitting && apiError && (
        <React.Fragment>
          <Alert error>{apiError}</Alert>
        </React.Fragment>
      )}
    </Modal>
  );
};

export default WaitingTimeModal;
